import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const CalendarBooking = () => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Days of the week
  const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // Time slots (restaurant hours)
  const timeSlots = [
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30',
  ];

  const handleSlotClick = (day, time) => {
    setSelectedSlot({ day, time });
    setShowConfirmation(false);
  };

  const handleReservar = () => {
    setShowConfirmation(true);
    // Here you would normally send the reservation to your backend
    setTimeout(() => {
      setShowConfirmation(false);
      setSelectedSlot(null);
    }, 3000);
  };

  const isSlotSelected = (day, time) => {
    if (!selectedSlot) return false;
    
    // Highlight the selected time slot (2-hour block)
    const selectedTimeIndex = timeSlots.indexOf(selectedSlot.time);
    const currentTimeIndex = timeSlots.indexOf(time);
    
    // Highlight 4 slots (2 hours) from the selected time
    return (
      selectedSlot.day === day &&
      currentTimeIndex >= selectedTimeIndex &&
      currentTimeIndex < selectedTimeIndex + 4
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 1, sm: 2, md: 1 },
        borderRadius: { xs: 0, sm: 2 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        m: { xs: 0, sm: 'auto' },
      }}
    >
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 500, 
          mb: { xs: 1, sm: 2 }, 
          textAlign: 'center',
          fontSize: { xs: '1.1rem', sm: '1.5rem' }
        }}
      >
        Calendario de Reservas
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'text.secondary', 
          mb: { xs: 1, sm: 2 }, 
          textAlign: 'center',
          fontSize: { xs: '0.75rem', sm: '0.875rem' }
        }}
      >
        Seleccione un día y horario. Se reservará por 2 horas.
      </Typography>

      {/* Calendar Grid */}
      <Box
        sx={{
          flexGrow: 1,
          overflowX: 'auto',
          overflowY: 'auto',
          mb: { xs: 1, sm: 2 },
          mx: { xs: -1, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `60px repeat(${days.length}, minmax(40px, 1fr))`,
            gap: { xs: 0.25, sm: 0.5 },
            minWidth: { xs: '100%', sm: '600px' },
          }}
        >
          {/* Header - Empty corner */}
          <Box />

          {/* Header - Days */}
          {days.map((day) => (
            <Box
              key={day}
              sx={{
                p: { xs: 0.5, sm: 1 },
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: { xs: 0.5, sm: 1 },
              }}
            >
              {day.substring(0, 3)}
            </Box>
          ))}

          {/* Time slots rows */}
          {timeSlots.map((time) => (
            <React.Fragment key={time}>
              {/* Time label */}
              <Box
                sx={{
                  p: { xs: 0.5, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                {time}
              </Box>

              {/* Day slots */}
              {days.map((day) => {
                // Sunday only has lunch service
                const isSunday = day === 'Domingo';
                const isEvening = parseInt(time.split(':')[0]) >= 19;
                const isDisabled = isSunday && isEvening;
                
                return (
                  <Box
                    key={`${day}-${time}`}
                    onClick={() => !isDisabled && handleSlotClick(day, time)}
                    sx={{
                      p: { xs: 0.25, sm: 0.5 },
                      minHeight: { xs: '25px', sm: '30px' },
                      border: 1,
                      borderColor: isSlotSelected(day, time) ? 'primary.main' : 'divider',
                      borderRadius: { xs: 0.5, sm: 1 },
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isDisabled
                        ? 'action.disabledBackground'
                        : isSlotSelected(day, time)
                        ? 'primary.light'
                        : 'background.paper',
                      opacity: isDisabled ? 0.3 : 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: isDisabled
                          ? 'action.disabledBackground'
                          : isSlotSelected(day, time)
                          ? 'primary.light'
                          : 'action.hover',
                      },
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Selected slot info */}
      {selectedSlot && (
        <Box sx={{ mb: { xs: 1, sm: 2 }, textAlign: 'center' }}>
          <Chip
            label={`${selectedSlot.day} a las ${selectedSlot.time} (2 horas)`}
            color="primary"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.9rem' }, 
              py: { xs: 1.5, sm: 2 },
              height: 'auto'
            }}
          />
        </Box>
      )}

      {/* Confirmation message */}
      {showConfirmation && (
        <Box
          sx={{
            mb: { xs: 1, sm: 2 },
            p: { xs: 1.5, sm: 2 },
            backgroundColor: 'success.light',
            borderRadius: { xs: 1, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <CheckCircleIcon sx={{ color: 'success.dark', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
          <Typography sx={{ 
            color: 'success.dark', 
            fontWeight: 500,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            ¡Reserva confirmada!
          </Typography>
        </Box>
      )}

      {/* Reserve button */}
      <Button
        variant="contained"
        color="secondary"
        size="large"
        onClick={handleReservar}
        disabled={!selectedSlot || showConfirmation}
        fullWidth
        sx={{
          py: { xs: 1.2, sm: 1.5 },
          textTransform: 'none',
          fontSize: { xs: '0.9rem', sm: '1rem' },
          borderRadius: { xs: 1, sm: 2 },
        }}
      >
        {showConfirmation ? 'Reservado' : 'Reservar'}
      </Button>
    </Paper>
  );
};

export default CalendarBooking;
